import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { CreateCreditPayload, Credit } from './usage-credit.models';

/**
 * Usage Credit data access — EB-3883.
 *
 * Thin typed wrapper over the invoice-api credit-controller
 * (`${environment.invoiceUrl}/credits`). No backend changes: all three
 * endpoints were probed live 2026-06-12 (see spec §API Integration Details).
 *
 * Credits are customer-scoped (BR-3) — `getCredits` filters by `customerId`.
 */
@Injectable({ providedIn: 'root' })
export class UsageCreditService {
  private readonly http = inject(HttpClient);

  private readonly base = environment.invoiceUrl;

  /**
   * List all credits for a customer.
   * `customerId` is sent as a string to match the API contract (responses also
   * carry it as a string).
   */
  getCredits(customerId: string): Observable<Credit[]> {
    const params = new HttpParams().set('customerId', customerId);
    return this.http.get<Credit[]>(`${this.base}/credits`, { params });
  }

  /**
   * Create a credit. The API returns the created entity with HTTP 200 (NOT
   * 201) — callers treat any 2xx as success (BR / Iteration 1 #10).
   */
  createCredit(payload: CreateCreditPayload): Observable<Credit> {
    return this.http.post<Credit>(`${this.base}/credits`, payload);
  }

  /**
   * Delete a credit by id. The backend rejects deletion of already-invoiced
   * credits; the UI also guards this client-side (BR-1).
   */
  deleteCredit(id: number): Observable<void> {
    return this.http.delete<void>(`${this.base}/credits/${id}`);
  }
}
